import {
  Box,
  Button,
  Typography,
  IconButton,
  Collapse,
  Checkbox,
  Menu,
  MenuItem,
  FormControlLabel,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Plus,
  UploadSimple,
  CaretUp,
  CaretDown,
  CaretLeft,
  CaretRight,
  PencilSimple,
  TrashSimple,
} from '@phosphor-icons/react';
import { useState } from 'react';
import StoreRowSkeleton from './StoreRowSkeleton';
import QuestionnaireDrawer from './QuestionnaireDrawer';
import KnowledgeBaseFileUploadModal from '../modals/KnowledgeBaseFileUploadModal';
import { useQuery } from '@tanstack/react-query';
import { getKnowledgeBaseFiles } from '../../utils/apis';
import DeleteModal from '../modals/DeleteModal';

interface NewFaqSetupUIProps {
  storesListLoading: boolean;
  storesList: any[];
  isQuestionnaireDrawerOpen: boolean;
  selectedStore: any;
  selectedStores: string[];
  isPending: boolean;
  onNext: () => void;
  onPrev: () => void;
  onCreateQuestionnaire: (store: any) => void;
  onCloseQuestionnaire: () => void;
  onSaveQuestionnaire: (questions: any[]) => void;
  onStoreSelect: (storeName: string) => void;
  onSelectAllStores: (checked: boolean, currentStore: any) => void;
  areAllStoresSelected: (currentStore: any) => boolean;
  onEditQuestion: (
    store: any,
    questionIndex: number,
    questionData: any
  ) => void;
  onDeleteQuestion: (store: any, questionData: any) => void;
  onDeletePending: boolean;
  deletingQuestionIds: number[];
  onDeleteFile?: (storeId: string, fileName: string) => Promise<void>;
}

export const NewFaqSetupUI = ({
  storesListLoading,
  storesList,
  isQuestionnaireDrawerOpen,
  selectedStore,
  selectedStores,
  isPending,
  onNext,
  onPrev,
  onCreateQuestionnaire,
  onCloseQuestionnaire,
  onSaveQuestionnaire,
  onStoreSelect,
  onSelectAllStores,
  areAllStoresSelected,
  onEditQuestion,
  onDeleteQuestion,
  onDeletePending,
  deletingQuestionIds,
  onDeleteFile,
}: NewFaqSetupUIProps) => {
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [selectedStoreForFiles, setSelectedStoreForFiles] = useState<any>(null);
  const [fileToDelete, setFileToDelete] = useState<{
    storeId: string;
    fileName: string;
  } | null>(null);
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: filesData, refetch } = useQuery({
    queryKey: ['get-store-knowledge-files', storesList],
    queryFn: async () => {
      // Fetch files for all stores in parallel
      const promises = storesList.map(store => getKnowledgeBaseFiles(store.id));
      const results = await Promise.all(promises);

      // Create a map of storeId -> files
      return results.reduce<Record<string, any>>((acc, result, index) => {
        // Access the files array from the nested structure
        acc[storesList[index].id] = result?.data?.files || [];
        return acc;
      }, {});
    },
    enabled: !!storesList?.length,
  });

  const StoreRow = ({ store }: { store: any }) => {
    const [expanded, setExpanded] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = (event?: React.MouseEvent<HTMLElement>) => {
      // Only close if it's an explicit close action (like clicking outside or cancel button)
      if (event?.type === 'click' && event.currentTarget === event.target) {
        setAnchorEl(null);
      }
    };

    const open = Boolean(anchorEl);

    const handleConfirmStoreSelection = () => {
      // Only proceed if there are selected stores and questions to copy
      if (
        selectedStores.length > 0 &&
        store.storeknowledgeBase?.qna?.length > 0
      ) {
        // Map the existing questions to the format expected by the API
        const questionsToSave = store.storeknowledgeBase.qna.map((q: any) => ({
          ques: q.ques,
          ans: q.ans,
        }));
        // Call onSaveQuestionnaire with the questions and selected stores
        //   @ts-expect-error
        onSaveQuestionnaire(questionsToSave, selectedStores);
      }
      handleClose();
    };

    const handleFileUploadClick = () => {
      setSelectedStoreForFiles(store);
      setIsFileModalOpen(true);
    };

    const storeFiles = filesData?.[store.id] || [];

    return (
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '6px',
          border: '1px solid',
          borderColor: '#DCE0E7',
          mb: 1,
          overflow: 'hidden',
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: '#5A4CE61A',
          }}
        >
          <Typography
            variant="body1"
            color="#2B2D3F"
            sx={{
              fontFamily: 'DM Sans',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '100%',
              letterSpacing: '-1%',
            }}
          >
            {store.friendly_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography
              variant="body2"
              color="#2B2D3F"
              sx={{ fontSize: '14px', fontWeight: 500 }}
            >
              Copy To
            </Typography>
            <Button
              size="small"
              onClick={handleClick}
              sx={{
                width: 'fit-content',
                height: 34,
                border: '1px solid',
                borderRadius: '6px',
                borderColor: '#DCE0E7',
                py: '8px',
                px: '8px',
                textTransform: 'none',
                bgcolor: '#F8F9FC',
                fontSize: '14px',
                fontWeight: 500,
                color: '#6B6F80',
              }}
              endIcon={
                <IconButton size="small" sx={{ p: 0, m: 0 }}>
                  <CaretDown size={15} />
                </IconButton>
              }
            >
              Select Stores
            </Button>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                minWidth: '30px',
                height: '30px',
                p: 0,
                borderRadius: '100%',
                bgcolor: '#FEFEFE',
                '&:hover': { bgcolor: '#FEFEEE' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
            </IconButton>
          </Box>
        </Box>

        {/* Stores Selection Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          keepMounted
          disableAutoFocusItem
          // disableCloseOnSelect
          slotProps={{
            paper: {
              sx: {
                width: '180px',
                mt: 1,
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              },
            },
          }}
        >
          <MenuItem
            dense
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onSelectAllStores(!areAllStoresSelected(store), store);
            }}
            sx={{ py: 0.5 }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={areAllStoresSelected(store)}
                  size="small"
                  indeterminate={
                    selectedStores.length > 0 && !areAllStoresSelected(store)
                  }
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: 'custom.textBlue',
                  }}
                >
                  All Stores
                </Typography>
              }
              sx={{ width: '100%', m: 0 }}
            />
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />

          {storesList.map((storeItem: any) => (
            <MenuItem
              key={storeItem.friendly_name}
              dense
              disabled={storeItem.friendly_name === store.friendly_name}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                if (storeItem.friendly_name !== store.friendly_name) {
                  onStoreSelect(storeItem.friendly_name);
                }
              }}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedStores.includes(storeItem.friendly_name)}
                    size="small"
                    disabled={storeItem.friendly_name === store.friendly_name}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px' }}>
                    {storeItem.friendly_name}
                  </Typography>
                }
                sx={{ width: '100%', m: 0 }}
              />
            </MenuItem>
          ))}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              p: 1,
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Button
              size="small"
              onClick={() => setAnchorEl(null)}
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                color: 'text.secondary',
                mr: 1,
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleConfirmStoreSelection}
              disabled={selectedStores.length === 0}
              sx={{
                fontSize: '12px',
                textTransform: 'none',
                boxShadow: 'none',
                borderRadius: '6px',
                px: 2,
              }}
            >
              Confirm
            </Button>
          </Box>
        </Menu>

        {/* Expandable Content */}
        <Collapse in={expanded}>
          <Box sx={{ py: 2, px: 4 }}>
            {/* Questions and Files List */}
            {(store.storeknowledgeBase?.qna?.length > 0 ||
              (storeFiles && storeFiles.length > 0)) && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#2B2D3F',
                    mb: 1,
                  }}
                >
                  Questions
                </Typography>

                {/* Questions */}
                {store.storeknowledgeBase?.qna?.map(
                  (qa: any, qIndex: number) => (
                    <Box
                      key={qa._id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        justifyContent: 'space-between',
                        py: 1,
                        px: 2,
                        bgcolor: '#F8F9FC',
                        borderRadius: '6px',
                        mb: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: '#2B2D3F',
                        }}
                      >
                        Question {qIndex + 1}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => onEditQuestion(store, qIndex, qa)}
                          sx={{
                            p: 1,
                            color: '#6B6F80',
                            '&:hover': { bgcolor: 'rgba(90, 76, 230, 0.04)' },
                          }}
                        >
                          <PencilSimple size={14} weight="duotone" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteQuestion(store, qa)}
                          disabled={
                            deletingQuestionIds.includes(qa.id) ||
                            onDeletePending
                          }
                          sx={{
                            minWidth: '30px',
                            height: '30px',
                            p: 0,
                            borderRadius: '6px',
                            color: '#E74C3C',
                            bgcolor: '#E74C3C26',
                            '&:hover': { bgcolor: '#E74C3C33' },
                          }}
                        >
                          {deletingQuestionIds.includes(qa.id) ? (
                            <CircularProgress size={14} />
                          ) : (
                            <TrashSimple size={14} weight="fill" />
                          )}
                        </IconButton>
                      </Box>
                    </Box>
                  )
                )}

                {/* Files */}
                {filesData?.[store.id]?.map((file: any) => (
                  <Box
                    key={file.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      px: 2,
                      bgcolor: '#F8F9FC',
                      borderRadius: '6px',
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: '14px', color: '#2B2D3F' }}>
                      {file.fileName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFileToDelete({
                          storeId: store.id,
                          fileName: file.fileName,
                        });
                        setIsDeleteModalOpen(true);
                      }}
                      disabled={isDeletingFile}
                      sx={{
                        minWidth: '30px',
                        height: '30px',
                        p: 0,
                        borderRadius: '6px',
                        color: '#E74C3C',
                        bgcolor: '#E74C3C26',
                        '&:hover': { bgcolor: '#E74C3C33' },
                      }}
                    >
                      {isDeletingFile &&
                      fileToDelete?.fileName === file.fileName ? (
                        <CircularProgress size={14} />
                      ) : (
                        <TrashSimple size={14} weight="fill" />
                      )}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                // borderTop: '1px solid',
                borderColor: 'grey.200',
                pt: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<Plus weight="bold" size={16} />}
                onClick={() => onCreateQuestionnaire(store)}
                sx={{
                  textTransform: 'none',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '-0.01em',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(90, 76, 230, 0.04)',
                  },
                }}
              >
                Add Question
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadSimple weight="bold" size={16} />}
                onClick={handleFileUploadClick}
                sx={{
                  textTransform: 'none',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  fontWeight: 500,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '-0.01em',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(90, 76, 230, 0.04)',
                  },
                }}
              >
                Upload File
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const handleDeleteFile = async () => {
    if (!fileToDelete || !onDeleteFile) return;

    setIsDeletingFile(true);
    try {
      await onDeleteFile(fileToDelete.storeId, fileToDelete.fileName);
      await refetch();
    } catch (error) {
      console.error('Failed to delete file:', error);
    } finally {
      setIsDeletingFile(false);
      setIsDeleteModalOpen(false);
      setFileToDelete(null);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          flex: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Typography
          color="#6B6F80"
          sx={{
            fontFamily: 'DM Sans',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '100%',
            letterSpacing: '-0.01em',
            textAlign: 'center',
            mb: 2,
          }}
        >
          Setup Knowledge Base for FAQ Agent across your stores
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {storesListLoading
            ? Array.from({ length: 3 }).map((_, index) => (
                <StoreRowSkeleton key={`skeleton-${index}`} />
              ))
            : storesList.map((store: any) => (
                <StoreRow key={store.id} store={store} />
              ))}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          p: 1,
          borderTop: '1px solid',
          borderColor: 'lightGray.main',
          bgcolor: 'background.paper',
        }}
      >
        <Button
          variant="outlined"
          startIcon={<CaretLeft size={16} />}
          onClick={onPrev}
          sx={{
            height: '34px',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            color: 'custom.textBlue',
            backgroundColor: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
        >
          Prev
        </Button>
        <Button
          variant="contained"
          endIcon={<CaretRight size={16} />}
          onClick={onNext}
          sx={{
            height: '34px',
            borderRadius: '8px',
            backgroundColor: 'primary.main',
            color: 'white',
            boxShadow: 'none',
            textTransform: 'none',
          }}
        >
          Next
        </Button>
      </Box>
      <QuestionnaireDrawer
        open={isQuestionnaireDrawerOpen}
        onClose={onCloseQuestionnaire}
        selectedStore={selectedStore}
        onSave={onSaveQuestionnaire}
        isPending={isPending}
      />
      <KnowledgeBaseFileUploadModal
        open={isFileModalOpen}
        onClose={() => {
          setIsFileModalOpen(false);
          refetch();
        }}
        selectedStore={selectedStoreForFiles}
      />
      <DeleteModal
        open={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setFileToDelete(null);
        }}
        onDelete={handleDeleteFile}
        isPending={isDeletingFile}
        title="Delete File"
        description="Are you sure this action cannot be undone and will permanently delete the uploaded file"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default NewFaqSetupUI;
